import { BASE_CURRENCY, ENDPIONTS, httpService, PaginatedResult } from "@api";
import { PaperHeader, PayrollSuspensions } from "@components";
import { useFetch } from "@hooks";
import { PayrollGroup, SubBranch } from "@models";
import { ComplexHeader, ExportData, Table, TableVerticalConfigs } from "@shared";
import { loansTotal, PayrollPreviewVM, recurringDeductionsTotal } from "@viewModels";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import PayrollApprovalPanel from "./PayrollApprovalPanel";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

type PayrollDetailsType = PayrollPreviewVM & {
  departmentName: string;
  branchName: string;
  payrollGroup: string;
  subBranchName: string;
  absenceDays: number;
  absenceAmount: number;
};

const PayrollDetails = () => {
  const payrollId = useParams().id;
  const MySwal = withReactContent(Swal);

  const [isLoading, setIsLoading] = useState(false);
  const [payrollDetails, setPayrollDetails] = useState<PayrollDetailsType[]>([]);
  const [subBranches, setsubBranches] = useState<SubBranch[]>([]);
  const [selectedPayrollGroup, setSelectedPayrollGroup] = useState("");
  const [selectedSubBranchId, setSelectedSubBranchId] = useState("");

  let fetchData = useFetch<PayrollDetailsType[]>({ endPoint: ENDPIONTS.payrollDetails, id: payrollId }, []);

  const fetchPayrollGroups = useFetch<PaginatedResult<PayrollGroup>>(
    {
      endPoint: ENDPIONTS.payrollGroups,
    },
    new PaginatedResult<PayrollGroup>()
  );

  useEffect(() => {
    if (fetchData?.data?.length > 0) {
      setPayrollDetails(fetchData.data);

      (async function () {
        // console.log("branch id is: ", fetchData?.data[0]?.branchId);
        const subBranches = await httpService(ENDPIONTS.subBranches).getById(fetchData?.data[0]?.branchId ?? 0);
        if (subBranches.status === 200) setsubBranches(subBranches.data);
      })();
    }
  }, [payrollId, fetchData.isFetching, fetchPayrollGroups.isFetching]);

  useEffect(() => {
    if (selectedPayrollGroup || selectedSubBranchId) {
      setPayrollDetails(
        (prev) =>
          [
            ...fetchData?.data
              ?.filterIf(selectedPayrollGroup !== "", (f) => f.payrollGroup === selectedPayrollGroup)
              .filterIf(selectedSubBranchId !== "", (f) => f.subBranchName === selectedSubBranchId),
          ] as PayrollDetailsType[]
      );
    } else {
      setPayrollDetails((prev) => [...fetchData?.data]);
    }
  }, [selectedPayrollGroup, selectedSubBranchId]);

  const headers: ComplexHeader[] = [
    { key: "branchName", title: "branch" },
    { key: "subBranchName", title: "Sub-Branch" },
    { key: "departmentName", title: "department" },
    { key: "payrollGroup", title: "payroll Group" },
    { key: "name", title: "Name" },
    {
      key: "employeeCategory",
      title: "Category",
      cellClass: (u: PayrollDetailsType) =>
        u.employeeCategory === "Others" ? "badge badge-warning" : "badge badge-info",
    },
    { key: "phone", title: "phone" },
    { key: "accountNumber", title: "account Number" },
    {
      key: "allowances",
      title: "Allowances",
      format: "currency",
      currency: () => BASE_CURRENCY,
      total: { format: "currency", currency: BASE_CURRENCY },
    },
    {
      key: "recurringDeductions",
      title: "deductions",
      compute: (row: any) => recurringDeductionsTotal(row),
      format: "currency",
      currency: () => BASE_CURRENCY,
      total: { format: "currency", currency: BASE_CURRENCY },
    },
    {
      key: "loans",
      title: "loans",
      compute: (row: any) => loansTotal(row),
      format: "currency",
      currency: () => BASE_CURRENCY,
      total: { format: "currency", currency: BASE_CURRENCY },
    },
    {
      key: "netSalary",
      title: "netSalary",
      format: "currency",
      currency: () => BASE_CURRENCY,
      total: { format: "currency", currency: BASE_CURRENCY },
    },
    {
      key: "absenceDays",
      title: "Absence (Days)",
      formatter: (value: any) => value === undefined || value === null ? "0" : value.toString(),
    },
    {
      key: "absenceAmount",
      title: "Absence Amount",
      format: "currency",
      currency: () => BASE_CURRENCY,
      total: { format: "currency", currency: BASE_CURRENCY },
    },
    {
      key: "netAmount",
      title: "netAmount",
      format: "currency",
      currency: () => BASE_CURRENCY,
      total: { format: "currency", currency: BASE_CURRENCY },
    },
  ];

  const onExport: ExportData = {
    data: () =>
      Promise.resolve(
        (fetchData.data ?? []).map(({ recurringDeductionsRef, ...row }: PayrollDetailsType) => ({
          ...row,
          deductions: recurringDeductionsTotal({ recurringDeductionsRef }),
          loans: loansTotal({ deductions: row.deductions, recurringDeductionsRef }),
        }))
      ),
    fileName: `Payroll Details Report - ${new Date().toLocaleDateString()}`,
  };

  const onPrint = (element: HTMLElement) => {
    var subElement = element.querySelectorAll(":scope > div")[0];
    subElement.classList.remove("d-none");

    var subElement02 = element.querySelectorAll(":scope > div")[1];
    subElement02.classList.add("d-none");

    let table = element.getElementsByTagName("table")[0];
    table.classList.remove("table-striped");
  };

  const Toast = Swal.mixin({
    toast: true,
    position: "bottom-end",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.onmouseenter = Swal.stopTimer;
      toast.onmouseleave = Swal.resumeTimer;
    },
  });

  const onGenerateReport = async () => {
    if (!payrollId) return Toast.fire({ icon: "error", title: "Error generating report" });

    setIsLoading(true);

    try {
      const params = new URLSearchParams();
      params.set("payrollId", payrollId);

      var res = await httpService(ENDPIONTS.generatePayrollSheet, params).getBlob();

      if (!res || !res.status || res.status !== 200) {
        setIsLoading(false);
        // Try to extract error message from blob response
        const errRes = res as any;
        if (errRes?.response?.data instanceof Blob) {
          try {
            const errorText = await errRes.response.data.text();
            const errorJson = JSON.parse(errorText);
            return Toast.fire({ icon: "error", title: errorJson?.message || "Error generating report" });
          } catch {
            return Toast.fire({ icon: "error", title: "Error generating report" });
          }
        }
        return Toast.fire({ icon: "error", title: "Error generating report" });
      }

      if (!res.data) {
        setIsLoading(false);
        return Toast.fire({ icon: "error", title: "Error generating report" });
      }

      const blob = new Blob([res.data], { type: "application/pdf" });
      const blobURL = URL.createObjectURL(blob);
      window.open(blobURL, "_blank");
    } catch (error: any) {
      Toast.fire({ icon: "error", title: error?.message || "Error generating report" });
    } finally {
      setIsLoading(false);
    }
  };

  const onViewSuspendedEmployees = async () => {
    if (!payrollId) return Toast.fire({ icon: "error", title: "Error fetching suspended employees" });

    MySwal.fire({
      showConfirmButton: false,
      allowOutsideClick: false,
      showCloseButton: true,
      width: 1000,
      html: <PayrollSuspensions payrollId={payrollId} />,
    });
  };

  return (
    <div className="content container-fluid">
      {payrollId && <PayrollApprovalPanel payrollId={payrollId} />}
      <div className="row align-items-center">
        <div className="d-flex justify-content-between mb-1">
          <div className="col">
            <h3 className="page-title">Payroll Details Report</h3>
          </div>
          <div className="col">
            <select
              className="form-control select"
              name="leaveType"
              onChange={(e) => setSelectedPayrollGroup(e.target.value)}
            >
              <option></option>
              {fetchPayrollGroups?.data?.items?.map((l, idx) => (
                <option key={idx} value={l.name}>
                  {l.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col">
            <select className="form-control select" onChange={(e) => setSelectedSubBranchId(e.target.value)}>
              <option></option>
              {subBranches?.map((p, i: any) => {
                return (
                  <option key={i} value={p.name}>
                    {p.name}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
      </div>
      <div className="col-12 mt-3" id="reportcontents">
        <PaperHeader title="Payroll Details Report" />
        <div className="row justify-content-between align-items-center mb-3">
          <div className="col-auto">
            <button className="btn btn-sm btn-outline-dark" disabled={isLoading} onClick={onViewSuspendedEmployees}>
              {!isLoading ? (
                "View Suspended Employees"
              ) : (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Loading...
                </>
              )}
            </button>
          </div>
          <div className="col-auto float-end ms-auto">
            <button className="btn btn-sm btn-dark" disabled={isLoading} onClick={onGenerateReport}>
              {!isLoading ? (
                "Print Report"
              ) : (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Loading...
                </>
              )}
            </button>
            <TableVerticalConfigs
              exportAsXSLS={onExport}
              onPrint={{
                ref: "reportcontents",
                prePrint: onPrint,
              }}
            />
          </div>
        </div>
        <Table
          // actions={actions}
          class="table table-striped table-hover mb-0"
          data={payrollDetails ?? []}
          headers={headers}
          isFetchingPage={fetchData.isFetching}
          showCounter
          // onPageChange={handlePageChange}
          paginationClass="row mt-3"
          // rowModifier={rowModifier}
          showTotals
        />
      </div>
    </div>
  );
};

export default PayrollDetails;
